import { FunctionsHttpError } from "@supabase/supabase-js";
import { AppError, toAppError } from "../../lib/appError";
import { supabase } from "../../lib/supabaseClient";
import { billingRepository, type Subscription } from "./billingRepository";

const callBillingFunction = async (name: string): Promise<string> => {
  const { data, error } = await supabase.functions.invoke(name, {
    method: "GET",
  });
  if (error) throw toAppError(error, "Could not reach the billing service.");
  const url = (data as { url?: string } | null)?.url;
  if (!url) {
    throw new AppError("The billing service returned no URL.", "NO_URL");
  }
  return url;
};

// polar-checkout refuses with 409 ALREADY_SUBSCRIBED when the user already has
// a live subscription (a second one would clobber the first).
const isAlreadySubscribed = async (error: unknown): Promise<boolean> => {
  const cause = error instanceof AppError ? error.cause : error;
  if (!(cause instanceof FunctionsHttpError) || cause.context.status !== 409) {
    return false;
  }
  try {
    const body = (await cause.context.clone().json()) as { code?: string };
    return body.code === "ALREADY_SUBSCRIBED";
  } catch {
    return false;
  }
};

export const subscriptionService = {
  /**
   * The row is for display only. Entitlement comes from the database helper so
   * the editor and the `publish_site` gate cannot disagree about the rules.
   */
  load: async (): Promise<{
    subscription: Subscription | null;
    isActive: boolean;
  }> => {
    const [subscription, isActive] = await Promise.all([
      billingRepository.getSubscription(),
      billingRepository.hasActiveSubscription(),
    ]);
    return { subscription, isActive };
  },

  /**
   * Someone who already subscribed is sent to the customer portal instead
   * (e.g. to undo a cancellation) rather than being charged twice.
   */
  checkoutUrl: async (): Promise<string> => {
    try {
      return await callBillingFunction("polar-checkout");
    } catch (error) {
      if (await isAlreadySubscribed(error)) {
        return callBillingFunction("polar-portal");
      }
      throw error;
    }
  },

  portalUrl: () => callBillingFunction("polar-portal"),
};
